import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, BadgeInfo, Copy, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import TradeInEditDialog from './trade-in-edit-dialog';

const TradeInDeleteMenuItem = ({
  dealId,
  tradeIn,
}: {
  dealId: Deal['id'];
  tradeIn: Deal['trades'][0];
}) => {
  const queryClient = useQueryClient();
  const tradeId = tradeIn.id;

  const deleteTradeInMutation = useMutation({
    onMutate: async () => {
      dealStore.removeTradeIn({
        id: dealId,
        tradeId,
      });
    },
    mutationFn: async ({
      tradeId,
    }: {
      tradeId: Deal['trades'][0]['id'];
    }): Promise<Deal['trades']> => {
      const route = `deals/${dealId}/trades/${tradeId}/delete`;
      // /deals/:id/trades/:trade_id/delete

      const response = await pdkAxios
        .post(`/${route}`, {
          // sales_reps: salesRepsIds,
        })
        .catch((e) => {
          console.error('Error deleting trade-in', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error deleting trade-in', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  return (
    <div
      className="flex flex-row gap-2 w-full"
      onClick={() => {
        deleteTradeInMutation.mutate({ tradeId });
      }}
    >
      <X className="size-3.5" />
      <div className="flex flex-col text-nowrap truncate">
        <p className="text-xs">
          Remove {tradeIn.year} {tradeIn.model}
        </p>
        {/* <p className="text-xs text-muted-foreground">{tradeIn.vin}</p> */}
      </div>
    </div>
  );
};

export const TradeInIcon = ({ className }: { className?: string }) => {
  return <ArrowRightLeft className={cn('size-3.5', className)} />;
};

export type DealTradeInsProps = {
  deal: Deal;
  orientation?: 'vertical' | 'horizontal';
  hideCopy?: boolean;
  className?: string;
};

const DealTradeIns = ({
  deal,
  orientation = 'vertical',
  hideCopy = false,
  className,
}: DealTradeInsProps) => {
  const isMobile = useIsMobile();

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [selectedTradeIn, setSelectedTradeIn] = useState<
    Deal['trades'][0] | null
  >(null);

  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [open3, setOpen3] = useState(false);

  const openMap: Record<number, boolean> = {
    1: open1,
    2: open2,
    3: open3,
  };
  const setOpenMap: Record<number, (open: boolean) => void> = {
    1: setOpen1,
    2: setOpen2,
    3: setOpen3,
  };
  const HOTKEYS_MAP: Record<number, string | [string, string]> = {
    1: HOTKEYS.TRADE_IN_1,
    2: HOTKEYS.TRADE_IN_2,
    3: HOTKEYS.TRADE_IN_3,
  };

  useHotkeys(HOTKEYS.TRADE_IN_1, (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen1(true);
  });

  useHotkeys(HOTKEYS.TRADE_IN_2, (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen2(true);
  });

  useHotkeys(HOTKEYS.TRADE_IN_3, (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen3(true);
  });

  return (
    <div
      className={cn(
        'flex items-center justify-start',
        {
          'flex-col': orientation === 'vertical',
          'flex-row': orientation === 'horizontal',
        },
        className,
      )}
    >
      {deal.trades?.map((tradeIn, index) => (
        <div
          key={tradeIn.id}
          className="flex flex-row gap-3 items-center w-full"
        >
          <DropdownMenu
            open={openMap[index + 1]}
            onOpenChange={(open: boolean) => {
              setOpenMap[index + 1](open);
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-1 h-fit">
                <ArrowRightLeft
                  className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                />
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    {!hideCopy && (
                      <p className="rounded text-xs  text-gray-600">
                        {tradeIn.year || 'Unknown'} {tradeIn.make || 'Unknown'}{' '}
                        {tradeIn.model || 'Unknown'}
                      </p>
                    )}
                  </TooltipTrigger>
                  <TooltipContent className="bg-white rounded px-2 py-2 border border-slate-300 z-50 text-xs flex flex-row gap-2 items-center">
                    Trade-In {index + 1}
                    <KeyboardKeyIcon>{HOTKEYS_MAP[index + 1]}</KeyboardKeyIcon>
                  </TooltipContent>
                </Tooltip>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Trade-In Actions
              </DropdownMenuLabel>
              <DropdownMenuItem>
                <TradeInDeleteMenuItem dealId={deal.id} tradeIn={tradeIn} />
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  // BUG NOTE: Radix has this bug where the whole application will freeze if you open the dialog
                  // within a dropdown menu... this is a voodoo workaround with setTimeout
                  // https://github.com/radix-ui/themes/issues/155#issuecomment-2476116687
                  setTimeout(() => {
                    setSelectedTradeIn(tradeIn);
                    setDialogIsOpen(true);
                  }, 0);
                }}
                className="flex flex-row gap-2 "
              >
                <div className="flex flex-row gap-2">
                  <Pencil className="size-3" />
                  <div className="flex flex-col text-nowrap truncate">
                    <p className="text-xs">
                      Edit {tradeIn.year} {tradeIn.model}
                    </p>
                    {/* <p className="text-xs text-muted-foreground">{tradeIn.vin}</p> */}
                  </div>
                </div>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  navigator.clipboard.writeText(tradeIn.vin);

                  toast({
                    description: (
                      <div className="flex flex-row gap-2 items-center">
                        <BadgeInfo className="size-4" />
                        <p>VIN copied to clipboard</p>
                      </div>
                    ),
                  });
                }}
                className="flex flex-row gap-2 "
              >
                <Copy className="size-3" />
                <div className="flex flex-col text-nowrap truncate">
                  <p className="text-xs">Copy Vin</p>
                  {/* <p className="text-xs text-muted-foreground">{tradeIn.vin}</p> */}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
      {selectedTradeIn && dialogIsOpen && (
        <TradeInEditDialog
          dealId={deal.id}
          tradeIn={selectedTradeIn}
          dialogIsOpen={dialogIsOpen}
          setDialogIsOpen={(open) => {
            setDialogIsOpen(open);
            if (!open) {
              setSelectedTradeIn(null);
            }
          }}
        ></TradeInEditDialog>
      )}
    </div>
  );
};

export default DealTradeIns;
